import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { MultiSelect } from "primereact/multiselect";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import { CustomInput } from "../../components/inputs/CustomInput";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { ConsoleTranslationsSchema } from "../../validations/schemas";
import {
  REVALIDATE_FORM_PROPS,
  TRANSLATION_LABELTYPE_OPTIONS,
} from "../../constants/index";
import {
  LANGUAGE_OPTIONS,
} from "../../constants";
import { AppContext } from "../../context";
import { useConsoleFunctionality } from "../../hooks/useConsoleFunctionalities";
import { useConsoleTranslation } from "../../hooks/useConsoleTranslations";
import { Header } from "../../components";
import { useNavigate } from "react-router-dom";

interface ConsoleTranslationProps {
  update?: boolean;
}

export const CTDefaultValues = {
  key: "",
  type: "",
  functionality: [],
  translationlanguage: "",
  translationvalue: "",
};

export const NewConsoleTranslation: React.FC<ConsoleTranslationProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedConsoleLabel },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const { translationAction, reloadFn } = useConsoleTranslation("DATA");
  const [consoleTranslationsFunctionalities, setConsoleTranslationsFunctionalities] = useState([]);
  const functionalities = useConsoleFunctionality('DATA')['data'];

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  useEffect(()=>{
   if (functionalities) {
    setConsoleTranslationsFunctionalities(functionalities.map(
      (item: { id:number, key:string, keylabel:string }) => ({
          label: t(`ConsoleTranslations.${item.key}OptionLabel`),
          value: item.key,
      })));
   }
  }, [functionalities]);

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onCreateTranslation = async () => {
    const formValues = getValues();
    const actionStatus = await translationAction(update ? "UPDATE" : "CREATE", {
      ...formValues,
      [update ? "updatedby" : "createdby"]: user?.name,
    });
    if (actionStatus === "SUCCESS") {
      reloadFn();
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`ConsoleTranslations.${update ? "save" : "create"}ModalTitle`),
          content: t(`ConsoleTranslations.${update ? "save" : "create"}ModalContent`),
          onConfirm: onCreateTranslation,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("ConsoleTranslations.cancelModalTitle"),
          content: t("ConsoleTranslations.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update && selectedConsoleLabel) {
      reset({
        id: selectedConsoleLabel.id,
        key: selectedConsoleLabel.key,
        type: selectedConsoleLabel.type,
        functionality: selectedConsoleLabel?.functionality,
        translationid: selectedConsoleLabel?.translationid,
        translationlanguage: selectedConsoleLabel?.translationlanguage,
        translationvalue: selectedConsoleLabel?.translationvalue,
      });
    } else {
      reset(CTDefaultValues);
    }
  }, [reset, selectedConsoleLabel, setAppState, update]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("ConsoleTranslations.editTranslationTitle") : t("ConsoleTranslations.newTranslationTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={`TransactionalSubheader.BreadCrumb.${t("ConsoleTranslations.agentsDatatableTitle")}`}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("ConsoleTranslations.adminConsoleMenuLabel"),
              disabled: true
            },
            {
              label: t("ConsoleTranslations.consoletranslationsTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
              ? t("ConsoleTranslations.editTranslationTitle")
              : t("ConsoleTranslations.newTranslationTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex align-items-center justify-content-center py-5"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
            <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
              {Object.keys(getValues())
                .filter((key) => !["id", "translationid"].includes(key))
                .map((key) => {
                  return (
                    <div 
                      key={key}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={key}
                        name={key}
                        control={control}
                        rules={(ConsoleTranslationsSchema(t) as any)[key]}
                        render={({ field }) => {
                          if (key === "functionality") {
                            field.onChange = (e) => {
                              const sortedValues = e.value.sort(
                                (a: string, b: string) => a.localeCompare(b)
                              );
                              setValue(
                                "functionality",
                                sortedValues,
                                REVALIDATE_FORM_PROPS
                              );
                            };
                          }

                          const extraProps = {
                            keyfilter: (ConsoleTranslationsSchema(t) as any)[key]
                              ?.keyfilter,
                            maxLength: (ConsoleTranslationsSchema(t) as any)[key]
                              ?.maxLength?.value,
                          };
                          if (key === "translationlanguage") {
                            return (
                              <div className='translation-fields' style={{ paddingLeft: '4rem' }}>
                                <Dropdown
                                  id={`dropdown-${key}`}
                                  className="dropdown mb-3"
                                  label={t(
                                    `ConsoleTranslations.translationlanguageLabel`
                                  )}
                                  placeholder={t(
                                    `ConsoleTranslations.translationlanguagePlaceholder`
                                  )}
                                  options={LANGUAGE_OPTIONS(t).sort((a, b) =>
                                    a.label.localeCompare(b.label)
                                  )}
                                  filter={false}
                                  showClear={false}
                                  style={{
                                    zIndex: "6000 !important",
                                  }}
                                  {...field}
                                />
                              </div>
                            );
                          } else if (key === "type") {
                            return (
                              <div className='translation-fields' style={{ paddingLeft: '4rem' }}>
                                <Dropdown
                                  id={`dropdown-${key}`}
                                  className="dropdown mb-3"
                                  label={t(`ConsoleTranslations.typeLabel`)}
                                  placeholder={t(`ConsoleTranslations.typePlaceholder`)}
                                  options={TRANSLATION_LABELTYPE_OPTIONS(t).sort(
                                    (a, b) => a.label.localeCompare(b.label)
                                  )}
                                  filter={false}
                                  showClear={false}
                                  style={{
                                    zIndex: "6000 !important",
                                  }}
                                  {...field}
                                />
                              </div>
                            );
                          } else if (key === "functionality") {
                            return (
                              <>
                                <label style={{ paddingLeft: '4rem' }} htmlFor={"functionality"}>
                                  {t(`ConsoleTranslations.functionalityLabel`)}
                                </label>
                                <div className='translation-fields' style={{ paddingLeft: '4rem' }}>
                                  <MultiSelect
                                    id={`multiselect-${key}`}
                                    display="chip"
                                    placeholder={t(
                                      `ConsoleTranslations.functionalityPlaceholder`
                                    )}
                                    className="pt-0"
                                    options={consoleTranslationsFunctionalities.sort(
                                      (a:any, b:any) => a.label.localeCompare(b.label)
                                    )}
                                    emptyFilterMessage={t(
                                      "ConsoleTranslations.noAvailableMessage"
                                    )}
                                    filter
                                    {...field}
                                  />
                                </div>
                              </>
                            );
                          } else {
                            return (
                              <div className='translation-fields' style={{ paddingLeft: '4rem' }}>
                                <CustomInput
                                  id={`input-label-${key}`}
                                  className={`${errors[key]?.message ? "p-invalid" : "mb-3"}`}
                                  label={t(`ConsoleTranslations.${key}Label`)}
                                  placeholder={t(
                                    `ConsoleTranslations.${key}Placeholder`
                                  )}
                                  type="text"
                                  autoComplete="off"
                                  // keyfilter={
                                  //   (WidgetCreatorSchema(t) as any)[key]?.keyfilter
                                  // }
                                  // maxLength={
                                  //   (WidgetCreatorSchema(t) as any)[key]?.maxLength?.value
                                  // }
                                  {...field}
                                  {...extraProps}
                                />
                              </div>
                            );
                          }
                        }}
                      />
                      <ErrorMessage
                        errors={errors}
                        name={key}
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            className="p-error block"
                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem", marginLeft: "4rem" }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    </div>
                  );
              })}
            </div>
            </div>
          </div>
        </div>
      </BoxFull>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("ConsoleTranslations.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            label={
              update
                ? t("ConsoleTranslations.editBtnLabel")
                : t("ConsoleTranslations.createBtnLabel")
            }
            variant="p-button"
            onClick={() => handleModalProps("action")}
            disabled={update ? !(isValid && isDirty) : !isValid}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
